import React, { useEffect, useMemo, useState, useId } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { ScryfallCard } from "@/types/cards";
import { auth, db } from "@/lib/firebase";
import {
  addDoc,
  doc,
  getDoc,
  updateDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebaseFunctions"; // CHANGED: use shared functions instance
import { listenBinders, addCardToBinder } from "@/data/binders";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "@/lib/firebase";
import ImageCropDialog from "@/components/media/ImageCropDialog";
import { prepareImageForSave } from "@/lib/cardImage";
import { useAuthUser, useAuthReady } from "@/lib/useAuthUser";
import { getAuthUser } from "@/lib/authGate";
import { scry, scryImageUrl } from "@/lib/scryClient";
import { enrichCardMeta } from "@/lib/scryfallEnrich";

// Simple LGS helpers (only if not already imported elsewhere)
type LGSPrefs = { offerContact?: "internal" | "website"; websiteBase?: string };
function buildLgsSearchUrl(p: LGSPrefs, cardName: string) {
  if (!p?.websiteBase) return null;
  const base = p.websiteBase.replace(/\/$/, "");
  return `${base}/search?q=${encodeURIComponent(cardName)}`;
}

// Normalize Response or JSON from backend scry client
async function norm<T = any>(x: any): Promise<T> {
  return typeof x?.json === "function" ? await x.json() : (x as T);
}

// Live vendor prices (TCG/CK) via configurable endpoint
function useVendorPrices(scryfallId?: string) {
  const endpoint = (import.meta as any).env?.VITE_CARD_PRICES_URL as string | undefined;
  const [state, setState] = useState({
    isLoading: false,
    error: null as string | null,
    endpointConfigured: !!endpoint,
    tcg: null as null | { low?: number | null; mid?: number | null; market?: number | null },
    cardkingdom: null as null | { low?: number | null; mid?: number | null; market?: number | null },
    averageMarket: null as number | null,
    updatedAt: null as number | null,
  });

  useEffect(() => {
    let alive = true;
    if (!endpoint || !scryfallId) {
      setState((s) => ({ ...s, endpointConfigured: !!endpoint }));
      return;
    }
    (async () => {
      try {
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const r = await fetch(`${endpoint.replace(/\/$/, "")}/prices/${encodeURIComponent(scryfallId)}`);
        if (!alive) return;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const tcg = j?.tcgplayer ?? {};
        const ck = j?.cardkingdom ?? {};
        const avg = [tcg.market, ck.market].filter((n: any) => typeof n === "number" && isFinite(n));
        const averageMarket = avg.length ? avg.reduce((a: number, b: number) => a + b, 0) / avg.length : null;
        setState((s) => ({
          ...s,
          isLoading: false,
          tcg: { low: tcg.low ?? null, mid: tcg.mid ?? null, market: tcg.market ?? null },
          cardkingdom: { low: ck.low ?? null, mid: ck.mid ?? null, market: ck.market ?? null },
          averageMarket,
          updatedAt: typeof j?.updatedAt === "number" ? j.updatedAt : j?.updatedAt ? Date.parse(j.updatedAt) : null,
        }));
      } catch (e: any) {
        if (!alive) return;
        setState((s) => ({ ...s, isLoading: false, error: e?.message ?? "Failed to load prices" }));
      }
    })();
    return () => {
      alive = false;
    };
  }, [endpoint, scryfallId]);

  return state;
}

const money = (n: number | null | undefined) =>
  typeof n === "number" && isFinite(n) ? `$${n.toFixed(2)}` : "—";

type Props = {
  open: boolean;
  card: any | null; // scryfall-ish selection
  onClose: () => void;
  onAdded?: () => void;
  // Edit support
  mode?: "add" | "edit";
  existing?: {
    docId: string;
    values: {
      qty: number;
      condition: "NM" | "LP" | "MP" | "HP" | "DMG";
      foil: boolean;
      for_sale: boolean;
      for_trade: boolean;
      price: number | null;
      notes: string;
      custom_image_url: string | null;
      status?: "sale" | "trade" | "keep";
    };
  };
  onUpdated?: () => void;
  initialPriceRegular?: number | null;
  initialPriceFoil?: number | null;
  /** @deprecated use initialPriceRegular */
  initialPrice?: number | null;
};

type Defaults = {
  qty: number;
  condition: "NM" | "LP" | "MP" | "HP" | "DMG";
  foil: boolean;
  sale: boolean;
  trade: boolean;
  remember: boolean;
};

const DEFAULTS_KEY = "sg_add_defaults";
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
type PriceDoc = Record<string, unknown>;

const asNum = (x: unknown): number | null => {
  const v =
    typeof x === "string" ? Number(x) : typeof x === "number" ? x : null;
  return Number.isFinite(v as number) ? (v as number) : null;
};
const pick = (keys: string[], obj: Record<string, unknown>) => {
  for (const k of keys) {
    const v = asNum(obj[k]);
    if (v != null) return v;
  }
  return null;
};
const pickMedian = (p: PriceDoc, foil: boolean) =>
  pick(
    foil
      ? [
          "usd_foil_median",
          "foil_usd_mid",
          "usd_foil_mid",
          "usd_foil",
          "foil_usd_median",
          "foil_usd",
        ]
      : ["usd_median", "usd_mid", "usd_market", "usd", "median"],
    p,
  ) ??
  pick(
    foil ? ["usd_foil_mid", "foil_mid"] : ["usd_mid", "mid"],
    (p.market as any) || {},
  ) ??
  pick(foil ? ["foil_mid"] : ["mid"], (p.tcgplayer as any) || {});
const pickLow = (p: PriceDoc, foil: boolean) =>
  pick(
    foil ? ["usd_foil_low", "foil_usd_low", "foil_low"] : ["usd_low", "low"],
    p,
  ) ??
  pick(
    foil ? ["usd_foil_low", "foil_low"] : ["usd_low", "low"],
    (p.market as any) || {},
  ) ??
  pick(foil ? ["foil_low"] : ["low"], (p.tcgplayer as any) || {});
const pickHigh = (p: PriceDoc, foil: boolean) =>
  pick(
    foil
      ? ["usd_foil_high", "foil_usd_high", "foil_high"]
      : ["usd_high", "high"],
    p,
  ) ??
  pick(
    foil ? ["usd_foil_high", "foil_high"] : ["usd_high", "high"],
    (p.market as any) || {},
  ) ??
  pick(foil ? ["foil_high"] : ["high"], (p.tcgplayer as any) || {});
const pickTcgDefault = (p: PriceDoc, foil: boolean) =>
  pick(
    foil
      ? [
          "usd_foil_market",
          "foil_usd_market",
          "foil_market",
          "usd_foil_mid",
          "foil_usd_mid",
          "foil_mid",
        ]
      : ["usd_market", "market", "usd_mid", "mid"],
    (p.tcgplayer as any) || {},
  ) ?? pickMedian(p, foil);

function compact<T extends Record<string, any>>(obj: T): Partial<T> {
  const out: Record<string, any> = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k as keyof T];
    if (v !== undefined && v !== null) out[k] = v;
  });
  return out as Partial<T>;
}

type FaceFields = {
  artist?: string | null;
  artist_id?: string | null;
  cmc?: number | null;
  color_indicator?: string[] | null;
  colors?: string[] | null;
  defense?: string | null;
  flavor_text?: string | null;
  illustration_id?: string | null;
  image_uris?: any | null; // keep Scryfall shape
  layout?: string | null;
  loyalty?: string | null;
  mana_cost?: string | null;
  name?: string | null;
  object?: string | null;
  oracle_id?: string | null;
  oracle_text?: string | null;
  power?: string | null;
  printed_name?: string | null;
  printed_text?: string | null;
  printed_type_line?: string | null;
  toughness?: string | null;
  type_line?: string | null;
  watermark?: string | null;
};

type Enriched = {
  parent: Partial<{
    type_line: string | null;
    colors: string[] | null;
    color_identity: string[] | null;
    mana_value: number | null;
    oracle_text: string | null;
    layout: string | null;
    image_small: string | null;
    image_normal: string | null;
    image_large: string | null;
  }>;
  faces?: FaceFields[];            // legacy local faces (kept)
  card_faces?: any[];             // Scryfall-native faces array
  scryfall_full?: any;            // full Scryfall JSON to future-proof
};

async function enrichScryfall(card: any): Promise<Enriched> {
  // Fetch full object via backend scry client
  let full: any = null;
  try {
    const res = await (scry as any).getById?.(card.id);
    full = await norm(res);
    full = full?.data ?? full ?? null;
  } catch {
    full = null;
  }

  const parentTypeLine = card?.type_line ?? full?.type_line ?? null;
  const parentColors = Array.isArray(card?.colors)
    ? card.colors
    : Array.isArray(full?.colors)
    ? full.colors
    : null;
  const parentColorIdentity = Array.isArray(card?.color_identity)
    ? card.color_identity
    : Array.isArray(full?.color_identity)
    ? full.color_identity
    : null;
  const mv =
    typeof card?.mana_value === "number"
      ? card.mana_value
      : typeof full?.mana_value === "number"
      ? full.mana_value
      : typeof full?.cmc === "number"
      ? full.cmc
      : null;
  const parentOracleText = card?.oracle_text ?? full?.oracle_text ?? null;
  const layout = card?.layout ?? full?.layout ?? null;

  // Keep embedded CDN URLs first; fallback to CDN-by-id via backend helper
  const image_small =
    card?.image_uris?.small ??
    card?.card_faces?.[0]?.image_uris?.small ??
    full?.image_uris?.small ??
    full?.card_faces?.[0]?.image_uris?.small ??
    (card?.id ? scryImageUrl(card.id, "small") : null) ??
    null;

  const image_normal =
    card?.image_uris?.normal ??
    card?.card_faces?.[0]?.image_uris?.normal ??
    full?.image_uris?.normal ??
    full?.card_faces?.[0]?.image_uris?.normal ??
    (card?.id ? scryImageUrl(card.id, "normal") : null) ??
    null;

  const image_large =
    card?.image_uris?.large ??
    card?.card_faces?.[0]?.image_uris?.large ??
    full?.image_uris?.large ??
    full?.card_faces?.[0]?.image_uris?.large ??
    (card?.id ? scryImageUrl(card.id, "large") : null) ??
    null;

  const facesSrc: any[] | null =
    Array.isArray(card?.card_faces) && card.card_faces.length
      ? card.card_faces
      : Array.isArray(full?.card_faces) && full.card_faces.length
      ? full.card_faces
      : null;

  const faces: FaceFields[] | undefined = facesSrc
    ? facesSrc.map((f) =>
        compact<FaceFields>({
          artist: f?.artist ?? null,
          artist_id: f?.artist_id ?? null,
          cmc:
            typeof f?.cmc === "number"
              ? f.cmc
              : typeof mv === "number"
              ? mv
              : null,
          color_indicator: Array.isArray(f?.color_indicator) ? f.color_indicator : null,
          colors: Array.isArray(f?.colors) ? f.colors : null,
          defense: f?.defense ?? null,
          flavor_text: f?.flavor_text ?? null,
          illustration_id: f?.illustration_id ?? null,
          image_uris: f?.image_uris ?? null, // keep Scryfall shape (CDN URIs)
          layout: f?.layout ?? null,
          loyalty: f?.loyalty ?? null,
          mana_cost: typeof f?.mana_cost === "string" ? f.mana_cost : null,
          name: f?.name ?? null,
          object: f?.object ?? null,
          oracle_id: f?.oracle_id ?? null,
          oracle_text: f?.oracle_text ?? null,
          power: f?.power ?? null,
          printed_name: f?.printed_name ?? null,
          printed_text: f?.printed_text ?? null,
          printed_type_line: f?.printed_type_line ?? null,
          toughness: f?.toughness ?? null,
          type_line: f?.type_line ?? null,
          watermark: f?.watermark ?? null,
        }),
      )
    : undefined;

  return {
    parent: compact({
      type_line: parentTypeLine,
      colors: parentColors,
      color_identity: parentColorIdentity,
      mana_value: mv,
      oracle_text: parentOracleText,
      layout,
      image_small,
      image_normal,
      image_large,
    }),
  faces,
  card_faces: Array.isArray(full?.card_faces) ? full.card_faces : undefined,
  scryfall_full: full || undefined,
  };
}

export default function AddToCollectionModal(props: Props) {
  const {
    open,
    card,
    onClose,
    onAdded,
    mode = "add",
    existing,
    onUpdated,
    initialPriceRegular,
    initialPriceFoil,
  } = props;

  const isEdit = mode === "edit" && !!existing?.docId;

  // Unify initial price into one variable used by existing code paths
  const initialPrice = useMemo<number | null>(() => {
    if (isEdit) {
      const p = existing?.values.price;
      return typeof p === "number" ? p : null;
    }
    if (typeof initialPriceRegular === "number") return initialPriceRegular;
    if (typeof initialPriceFoil === "number") return initialPriceFoil;
    return null;
  }, [isEdit, existing?.values.price, initialPriceRegular, initialPriceFoil]);

  // Early guards: avoid rendering when required inputs aren't ready
  if (!open) return null;
  if (!isEdit && !card) return null;

  const navigate = useNavigate();
  const location = useLocation();

  // Allow empty string while typing so backspace works
  const [qty, setQty] = useState<number | "">(1);
  const [condition, setCondition] = useState<Defaults["condition"]>("NM");
  const [foil, setFoil] = useState<boolean>(false);
  const [sale, setSale] = useState<boolean>(false);
  const [trade, setTrade] = useState<boolean>(false);
  const { tcg, cardkingdom, averageMarket, updatedAt: vendorUpdatedAt, isLoading: vendorLoading, error: vendorError, endpointConfigured } = useVendorPrices(card?.id);
const [price, setPrice] = useState<number | "">("");
const [userTouchedPrice, setUserTouchedPrice] = useState(false);

// One-time default when pricing arrives (TCG market preferred, else average market)
useEffect(() => {
  if (userTouchedPrice || vendorLoading) return;
  if (typeof tcg?.market === "number") {
    setPrice(tcg.market);
  } else if (typeof averageMarket === "number") {
    setPrice(averageMarket);
  }
}, [tcg?.market, averageMarket, vendorLoading, userTouchedPrice]);

  const [notes, setNotes] = useState<string>("");

  const [remember, setRemember] = useState<boolean>(false);
  const [priceDirty, setPriceDirty] = useState<boolean>(false);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);

  const [priceDoc, setPriceDoc] = useState<PriceDoc | null>(null);
  const [priceLoading, setPriceLoading] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  const [modChecking, setModChecking] = useState<boolean>(false);
  const [modErr, setModErr] = useState<string | null>(null);

  const [binders, setBinders] = useState<{ id: string; name: string }[]>([]);
  const [binderId, setBinderId] = useState<{ id: string; name: string }[] | any>(""); // existing line in your code, left intact

  // Cropper UI state
  const [cropOpen, setCropOpen] = useState(false);

  // Load remembered defaults
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DEFAULTS_KEY);
      if (!raw) return;
      const d = JSON.parse(raw) as Partial<Defaults>;
      if (d.qty) setQty(d.qty);
      if (d.condition) setCondition(d.condition);
      if (typeof d.foil === "boolean") setFoil(d.foil);
      if (typeof d.sale === "boolean") setSale(d.sale);
      if (typeof d.trade === "boolean") setTrade(d.trade);
      if (typeof d.remember === "boolean") setRemember(d.remember);
    } catch {}
  }, []);

  // Fetch pricing doc
  useEffect(() => {
    let alive = true;
    if (!card?.id) { setPriceDoc(null); return; }
    (async () => {
      try {
        setPriceLoading(true);
        const snap = await getDoc(doc(db, "prices", card.id!));
        if (!alive) return;
        setPriceDoc(snap.exists() ? (snap.data() as PriceDoc) : null);
      } catch {
        if (alive) setPriceDoc(null);
      } finally {
        if (alive) setPriceLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [card?.id]);

  const uid = auth.currentUser?.uid ?? null;
  useEffect(() => {
    if (!open || !uid) return;
    return listenBinders((b) => setBinders(b.map(({ id, name }) => ({ id, name }))), uid);
  }, [open, uid]);

  const median = useMemo(
    () => pickMedian(priceDoc ?? {}, foil),
    [priceDoc, foil],
  );
  const low = useMemo(() => pickLow(priceDoc ?? {}, foil), [priceDoc, foil]);
  const high = useMemo(() => pickHigh(priceDoc ?? {}, foil), [priceDoc, foil]);
  const tcgDefault = useMemo(
    () => pickTcgDefault(priceDoc ?? {}, foil),
    [priceDoc, foil],
  );

  // Reset price state when the modal opens for a card
  useEffect(() => {
    if (open) {
      setPriceDirty(false);
      setPrice("");
    }
  }, [open, card.id]);

  // Prefill from variant’s provided prices / tcg / median
  useEffect(() => {
    if (!open) return;
    if (foil) {
      if (initialPriceFoil != null && Number.isFinite(initialPriceFoil)) {
        setPrice(Number((Math.round(initialPriceFoil * 100) / 100).toFixed(2)));
        setPriceDirty(true);
      } else if (tcgDefault != null) {
        setPrice(Number(tcgDefault.toFixed(2)));
        setPriceDirty(true);
      } else if (median != null) {
        setPrice(Number(median.toFixed(2)));
        setPriceDirty(true);
      }
    } else {
      const seed = initialPriceRegular ?? initialPrice;
      if (seed != null && Number.isFinite(seed)) {
        const val = Math.round(seed * 100) / 100;
        setPrice(val);
        setPriceDirty(true);
      } else if (tcgDefault != null) {
        setPrice(Number(tcgDefault.toFixed(2)));
        setPriceDirty(true);
      } else if (median != null) {
        setPrice(Number(median.toFixed(2)));
        setPriceDirty(true);
      }
    }
  }, [
    open,
    foil,
    tcgDefault,
    median,
    initialPriceRegular,
    initialPriceFoil,
    initialPrice,
    card.id,
  ]);

  // Image preview
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const priceNumber = useMemo(() => (price === "" ? null : Number(price)), [price]);
  // Normalize qty for persistence
  const qtyNumber = useMemo(() => {
    return typeof qty === "number" && Number.isFinite(qty) && qty >= 1
      ? Math.floor(qty)
      : 1;
  }, [qty]);

 const defaultArt =
   card?.card_faces?.[0]?.image_uris?.normal ??
   card?.image_uris?.normal ??
   card?.card_faces?.[0]?.image_uris?.large ??
   card?.image_uris?.large ??
   (card?.id ? scryImageUrl(card.id, "normal") : null);

 function openOracle() {
   if (!card?.id) return;
   navigate(`/search/oracle/${encodeURIComponent(card.id)}`, {
     state: { backgroundLocation: location },
   });
 }

  function extForType(t: string): string {
    if (t === "image/png") return ".png";
    if (t === "image/webp") return ".webp";
    return ".jpg";
  }

  // Resize on client to cap payload size before moderation
  async function fileToBase64Resized(f: File, maxSide = 768, quality = 0.85): Promise<string> {
    const img = document.createElement("img");
    const url = URL.createObjectURL(f);
    try {
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error("Failed to load image"));
        img.src = url;
      });
      const { width, height } = img;
      const scale = Math.min(1, maxSide / Math.max(width, height));
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not supported");
      ctx.drawImage(img, 0, 0, w, h);
      // JPEG reduces size and is enough for SafeSearch
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      // Strip prefix: "data:image/jpeg;base64,"
      const base64 = dataUrl.split(",")[1] || "";
      return base64;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  async function precheckImage(file: File): Promise<void> {
    setModErr(null);
    setModChecking(true);
    try {
      const imgB64 = await fileToBase64Resized(file);
      const call = httpsCallable(functions, "moderateImage");
      const resp = (await call({
        imageBase64: imgB64,
        // optional thresholds override per-call
        thresholds: { adult: "POSSIBLE", violence: "POSSIBLE", racy: "LIKELY" },
        context: { where: "addToCollection", scryfall_id: card?.id ?? null, name: card?.name ?? null },

      })) as any;
      const { allowed, reasons } = resp?.data || {};
      if (!allowed) {
        const reasonText = Array.isArray(reasons) && reasons.length ? reasons.join(", ") : "Inappropriate content";
        throw new Error(`Image blocked by content protection (${reasonText}).`);
      }
    } finally {
      setModChecking(false);
    }
  }

  // Upload selected image, compressing client-side first. Returns URL and storage path.
  async function uploadIfNeeded(uid: string): Promise<{ url: string; path: string } | null> {
    if (!file) return null;

    // Content protection: pre-check before any upload
    await precheckImage(file);

    if (!ACCEPTED_TYPES.includes(file.type))
      throw new Error("Unsupported image type. Use PNG, JPG/JPEG, or WEBP.");
    if (file.size > 10 * 1024 * 1024)
      throw new Error("Image is too large. Max 10MB.");

    const filename = `${card?.id ?? "custom"}_${Date.now()}${extForType(file.type)}`.replace(
      /[^a-zA-Z0-9._-]/g,
      "_",
    );
    const objectPath = `users/${auth.currentUser!.uid}/cardImages/${filename}`;
    const storageRef = ref(storage, objectPath);

    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        scryfall_id: card.id,
        owner_uid: auth.currentUser!.uid,
        card_name: card.name,
        moderation: "approved", // pre-checked client-side; still server-verified
      },
    });

    setUploadProgress(0);
    const url: string = await new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (snap) =>
          setUploadProgress(
            Math.round((snap.bytesTransferred / snap.totalBytes) * 100),
          ),
        (err) => reject(err),
        async () => resolve(await getDownloadURL(task.snapshot.ref)),
      );
    });

    setUploadedUrl(url);
    return { url, path: objectPath };
  }
  function rememberDefaults() {
    try {
      const toStore: Defaults = {
        qty: qtyNumber,
        condition,
        foil,
        sale,
        trade,
        remember,
      };
      localStorage.setItem(DEFAULTS_KEY, JSON.stringify(toStore));
    } catch {}
  }

  async function save() {
    if (!auth.currentUser) {
      setErr("Please sign in.");
      return;
    }
    const uid = auth.currentUser.uid;

    setSaving(true);
    setErr(null);
    setModErr(null);

    try {
      if (remember) rememberDefaults();

      let uploadedInfo: { url: string; path: string } | null = null;
      let finalImage = uploadedUrl;
      if (!finalImage && file) {
        try {
          uploadedInfo = await uploadIfNeeded(uid);
          finalImage = uploadedInfo?.url ?? null;
        } catch (e: any) {
          setModErr(e?.message ?? "Image failed moderation.");
          throw e;
        }
      }

      // Enrich missing Scryfall fields without overwriting existing selection fields
      const { parent: enrichParent, faces: enrichFaces, card_faces, scryfall_full } = await enrichScryfall(card);

      const toCents = (n: number | null) => (typeof n === "number" ? Math.round(n * 100) / 100 : null);
      const resolvedPrice = sale
        ? toCents(priceNumber ?? (tcgDefault != null ? Number(tcgDefault.toFixed(2)) : null))
        : null;

      const payload: any = {
        scryfall_id: card.id,
        oracle_id: card.oracle_id ?? null,
        name: String(card.name || "").trim(),
        name_lower: String(card.name || "").trim().toLowerCase(),
        set: card.set,
        number: card.collector_number ?? null,
        image: finalImage ?? defaultArt,
        custom_image_url: uploadedInfo?.url ?? uploadedUrl ?? null,
        custom_image_path: uploadedInfo?.path ?? null,
        // Parent metadata: only add if available
        ...(enrichParent.type_line ? { type_line: enrichParent.type_line } : {}),
        ...(enrichParent.colors ? { colors: enrichParent.colors } : {}),
        ...(enrichParent.color_identity ? { color_identity: enrichParent.color_identity } : {}),
        ...(typeof enrichParent.mana_value === "number" ? { mana_value: enrichParent.mana_value } : {}),
        ...(enrichParent.oracle_text ? { oracle_text: enrichParent.oracle_text } : {}),
        ...(enrichParent.layout ? { layout: enrichParent.layout } : {}),
        ...(enrichParent.image_small ? { image_small: enrichParent.image_small } : {}),
        ...(enrichParent.image_normal ? { image_normal: enrichParent.image_normal } : {}),
        ...(enrichParent.image_large ? { image_large: enrichParent.image_large } : {}),
        // Faces metadata (both legacy `faces` and Scryfall-native `card_faces`)
        ...(Array.isArray(enrichFaces) && enrichFaces.length ? { faces: enrichFaces } : {}),
        ...(Array.isArray(card_faces) && card_faces.length ? { card_faces } : {}),
        // Store the entire Scryfall object for future-proofing
        ...(scryfall_full ? { scryfall_full } : {}),
        foil,
        condition,
        qty: qtyNumber,
        for_sale: Boolean(sale),
        for_trade: Boolean(trade),
        status: sale ? "sale" : trade ? "trade" : "keep",
        price: resolvedPrice,
        notes: notes || null,
        createdAt: serverTimestamp(),
      };

      // LGS-only stamping
      if (isLGS) {
        payload.status = "sale";
        payload.for_sale = true;
        payload.for_trade = false;
        payload.ownerIsLGS = true;
        const offerContact = lgsPrefs?.offerContact ?? "internal";
        payload.offerContact = offerContact;
        if (offerContact === "website" && lgsPrefs) {
          payload.externalOfferUrl = buildLgsSearchUrl(lgsPrefs, card.name);
        }
      }

      await addDoc(collection(db, "users", uid, "cards"), payload);
      onAdded?.();
      setUploadProgress(null);
      setUploadedUrl(null);
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? "Could not save card.");
    } finally {
      setSaving(false);
    }
  }

  // Prefill form when editing
  useEffect(() => {
    if (!open || mode !== "edit" || !existing) return;
    try {
      // Example: if you have local state like qty, condition, foil, for_sale, for_trade, price, notes, imageUrl, etc.
      setQty(existing.values.qty);
      setCondition(existing.values.condition);
      setFoil(!!existing.values.foil);
      setSale(!!existing.values.for_sale);
      setTrade(!!existing.values.for_trade);
      setPrice(existing.values.price ?? "");
      setNotes(existing.values.notes ?? "");
      setUploadedUrl(existing.values.custom_image_url ?? null);
    } catch {}
  }, [open, mode, existing?.docId]);

useEffect(() => { if (!sale) setPrice(""); }, [sale]);

  // On confirm, branch: add vs update
  async function onConfirm() {
    if (isEdit && existing?.docId) {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const ref = doc(db, "users", uid, "cards", existing.docId);
      const payload: any = {
        // keep your schema field names
        qty: qtyNumber,
        condition,
        foil,
        for_sale: sale,
        for_trade: trade,
        price: sale ? (price === "" ? null : Number(price)) : null,
        notes,
        status: sale ? "sale" : trade ? "trade" : "keep",
        name_lower: (existing?.values as any)?.name?.toLowerCase?.() ?? (card?.name?.toLowerCase?.() ?? undefined),
        updatedAt: serverTimestamp(),
      };

      // LGS-only stamping
      if (isLGS) {
        payload.status = "sale";
        payload.for_sale = true;
        payload.for_trade = false;
        payload.ownerIsLGS = true;
        const offerContact = lgsPrefs?.offerContact ?? "internal";
        payload.offerContact = offerContact;
        if (offerContact === "website" && lgsPrefs) {
          payload.externalOfferUrl = buildLgsSearchUrl(
            lgsPrefs,
            existing?.values?.name || (card as any)?.name || ""
          );
        }
      }

      await updateDoc(ref, payload);
      if (binderId && isEdit && existing?.docId && uid) await addCardToBinder(binderId, existing.docId, uid);
      onUpdated?.();
      return;
    }
    if (!card?.id) {
      return;
    }
    const uid = auth.currentUser?.uid ?? null;
    if (!uid) return;

    let uploadedInfo: { url: string; path: string } | null = null;
    let finalImage = uploadedUrl;
    if (!finalImage && file) {
      try {
        uploadedInfo = await uploadIfNeeded(uid);
        finalImage = uploadedInfo?.url ?? null;
      } catch (e: any) {
        setModErr(e?.message ?? "Image failed moderation.");
        throw e;
      }
    }

    // Enrich missing Scryfall fields without overwriting what's already on the selected card
    const { parent: enrichParent, faces: enrichFaces, card_faces, scryfall_full } = await enrichScryfall(card);

    const toCents = (n: number | null) => (typeof n === "number" ? Math.round(n * 100) / 100 : null);
    const resolvedPrice = sale
      ? toCents(priceNumber ?? (tcgDefault != null ? Number(tcgDefault.toFixed(2)) : null))
      : null;

    const payload: any = {
      scryfall_id: card.id,
      oracle_id: card.oracle_id ?? null,
      name: card.name,
      name_lower: card.name?.toLowerCase?.() ?? null, // NEW
      set: card.set,
      number: card.collector_number ?? null,
      image: finalImage ?? defaultArt,
      ...(enrichParent.type_line ? { type_line: enrichParent.type_line } : {}),
      ...(enrichParent.colors ? { colors: enrichParent.colors } : {}),
      ...(enrichParent.color_identity ? { color_identity: enrichParent.color_identity } : {}),
      ...(typeof enrichParent.mana_value === "number" ? { mana_value: enrichParent.mana_value } : {}),
      ...(enrichParent.oracle_text ? { oracle_text: enrichParent.oracle_text } : {}),
      ...(enrichParent.layout ? { layout: enrichParent.layout } : {}),
      ...(enrichParent.image_small ? { image_small: enrichParent.image_small } : {}),
      ...(enrichParent.image_normal ? { image_normal: enrichParent.image_normal } : {}),
      ...(enrichParent.image_large ? { image_large: enrichParent.image_large } : {}),
     ...(Array.isArray(enrichFaces) && enrichFaces.length ? { faces: enrichFaces } : {}),
     ...(Array.isArray(card_faces) && card_faces.length ? { card_faces } : {}),
     ...(scryfall_full ? { scryfall_full } : {}),
      foil,
      condition,
      qty: qtyNumber,
      for_sale: Boolean(sale),
      for_trade: Boolean(trade),
      status: sale ? "sale" : trade ? "trade" : "keep",
      price: resolvedPrice,
      notes: notes || null,
      createdAt: serverTimestamp(),
    };

    // LGS-only stamping
    if (isLGS) {
      payload.status = "sale";
      payload.for_sale = true;
      payload.for_trade = false;
      payload.ownerIsLGS = true;
      const offerContact = lgsPrefs?.offerContact ?? "internal";
      payload.offerContact = offerContact;
      if (offerContact === "website" && lgsPrefs) {
        payload.externalOfferUrl = buildLgsSearchUrl(lgsPrefs, card.name);
      }
    }

    const { id: createdId } = await addDoc(collection(db, "users", uid, "cards"), payload);
    if (binderId && createdId && uid) await addCardToBinder(binderId, createdId, uid);
    onAdded?.();
    setUploadProgress(null);
    setUploadedUrl(null);
    onClose();
  }

  // Close on ESC
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const titleId = useId();

  // Save to Wishlist using same image selection and enrichment
  async function saveToWishlist() {
    if (!auth.currentUser) { setErr("Please sign in."); return; }
    const uid = auth.currentUser.uid;
    setSaving(true);
    setErr(null);
    setModErr(null);
    try {
      const img = await prepareImageForSave({
        uid,
        card,
        file,
        uploadedUrl,
        onProgress: (p) => setUploadProgress(p),
      });
      const { parent: enrichParent, faces: enrichFaces, card_faces, scryfall_full } = await enrichScryfall(card);
      const payload: any = {
        scryfall_id: card.id,
        oracle_id: card.oracle_id ?? null,
        name: String(card.name || "").trim(),
        name_lower: String(card.name || "").trim().toLowerCase(),
        set: card.set,
        number: card.collector_number ?? null,
        image: img.image,
        custom_image_url: img.custom_image_url ?? null,
        custom_image_path: img.custom_image_path ?? null,
        ...(enrichParent.type_line ? { type_line: enrichParent.type_line } : {}),
        ...(enrichParent.colors ? { colors: enrichParent.colors } : {}),
        ...(enrichParent.color_identity ? { color_identity: enrichParent.color_identity } : {}),
        ...(typeof enrichParent.mana_value === "number" ? { mana_value: enrichParent.mana_value } : {}),
        ...(enrichParent.oracle_text ? { oracle_text: enrichParent.oracle_text } : {}),
        ...(enrichParent.layout ? { layout: enrichParent.layout } : {}),
        ...(enrichParent.image_small ? { image_small: enrichParent.image_small } : {}),
        ...(enrichParent.image_normal ? { image_normal: enrichParent.image_normal } : {}),
        ...(enrichParent.image_large ? { image_large: enrichParent.image_large } : {}),
     ...(Array.isArray(enrichFaces) && enrichFaces.length ? { faces: enrichFaces } : {}),
     ...(Array.isArray(card_faces) && card_faces.length ? { card_faces } : {}),
     ...(scryfall_full ? { scryfall_full } : {}),
        foil,
        condition,
        desired_qty: qtyNumber,
        notes: notes || null,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, "users", uid, "wishlist"), payload);
      onAdded?.();
      setUploadProgress(null);
      setUploadedUrl(null);
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? "Could not add to wishlist.");
    } finally {
      setSaving(false);
    }
  }

  const authUser = useAuthUser();
  const authReady = useAuthReady();
  const [needsSignIn, setNeedsSignIn] = useState(false);

  // Example: guard any loader / mutation that previously logged "Sign in first."
  async function loadUserCollection() {
    const u = getAuthUser();
    if (!auth.currentUser) {
  setNeedsSignIn?.(true);
  return;
    }
    // proceed (no artificial wait)
  }

  // Wherever it previously ran immediately (e.g. useEffect(() => { loadUserCollection(); }, [])):
  useEffect(() => {
    const u = getAuthUser();
    if (!u) {
      // run once only (avoid spam)
      setNeedsSignIn(true);
      return;
    }
    void loadUserCollection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // runOnce; runAfterAuth internally waits

 
  // --- LGS (applies only to LGS users) ---
  const [isLGS, setIsLGS] = useState(false);
  const [lgsPrefs, setLgsPrefs] = useState<LGSPrefs | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const u = auth.currentUser;
        if (!u) { setIsLGS(false); setLgsPrefs(null); return; }
        const tok = await u.getIdTokenResult(true);
        const flag = !!(tok.claims as any)?.isLGS;
        setIsLGS(flag);
        if (flag) {
          const snap = await getDoc(doc(db, "users", u.uid));
          setLgsPrefs((snap.data() as any)?.lgs ?? null);
        } else {
          setLgsPrefs(null);
        }
      } catch {
        setIsLGS(false);
        setLgsPrefs(null);
      }
    })();
  }, [props.open]);
  // --- end LGS ---

  // Ensure sale/trade reflect LGS lock (no effect for regular users)
  useEffect(() => {
    if (isLGS) {
      setSale(true);
      setTrade(false);
    }
  }, [isLGS, props.open]);

  // ...existing JSX toggles...
  // Sale button: onClick={() => { if (!isLGS) setSale((v) => !v); }} disabled={isLGS} label "For Sale (LGS-locked)" when isLGS
  // Trade button: onClick={() => { if (!isLGS) setTrade((v) => !v); }} disabled={isLGS}

  return (
    <>
      <div
        className="fixed inset-0 z-[200] grid place-items-center p-3 sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div
          className="absolute inset-0 bg-black/70"
          onClick={(e) => {
            e.stopPropagation();
            onClose();        // closes only AddToCollection level (returns to zoom if present)
          }}
        />
        <div
          className="relative z-10 w-full max-w-[min(720px,92vw)] max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]
                     rounded-2xl border border-white/10 bg-gray-900/95 backdrop-blur shadow-2xl overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="px-4 py-3 border-b border-white/10 flex items-center justify-between shrink-0">
            <h3 id={titleId} className="text-white font-semibold truncate">
              {mode === "edit" ? "Edit card" : "Add to Collection"}
            </h3>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </header>

          <div className="px-4 sm:px-5 py-4 overflow-y-auto">
            {needsSignIn && (
              <div className="mb-3 p-3 rounded bg-white/5 text-sm text-white/80 text-center">
                Please sign in to manage your collection.
              </div>
            )}
            {/* Card header row */}
            <div className="flex items-start gap-3">
              {defaultArt && (
                <div className="shrink-0">
                  <img
                    src={defaultArt}
                    alt={card?.name ?? "Card"}
                    className="w-24 h-32 object-cover rounded border border-gray-700 cursor-pointer"
                    onClick={openOracle}
                  />
                  <div className="mt-2 text-[11px] text-white/60 text-center">
                    Click art to open Oracle
                  </div>
                </div>
              )}

              <div className="min-w-0 flex-1">
                <div className="text-white font-medium text-sm sm:text-base truncate">
                  {card?.name ?? "Card"}
                </div>
                <div className="text-white/70 text-xs sm:text-sm truncate">
                  {(card?.set || "").toUpperCase()} • #{card?.collector_number ?? ""}
                </div>

                {err && (
                  <div className="mt-2 text-[13px] text-amber-300">{err}</div>
                )}
                {modChecking && (
                  <div className="mt-2 text-[13px] text-sky-300">Scanning image for safety…</div>
                )}
                {modErr && (
                  <div className="mt-2 text-[13px] text-red-300">{modErr}</div>
                )}

                {/* Pricing strip */}
                <div className="mt-3 rounded-md border border-white/10 bg-white/5 p-2.5">
                  <div className="flex items-center justify-between">
                    <div className="text-white/90 font-medium text-sm">
                      Market Pricing {foil ? "(Foil)" : ""}
                    </div>
                    {!endpointConfigured && (
                      <div className="text-xs text-amber-300">Endpoint not configured</div>
                    )}
                    {vendorLoading && endpointConfigured && (
                      <div className="text-xs text-white/60">Loading…</div>
                    )}
                    {vendorError && (
                      <div className="text-xs text-red-300">Error</div>
                    )}
                  </div>

                  {endpointConfigured && !vendorLoading && !vendorError && (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] text-white/80">
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">TCG Low</div>
                        <div className="font-semibold">{money(tcg?.low)}</div>
                      </div>
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">TCG Mid</div>
                        <div className="font-semibold">{money(tcg?.mid)}</div>
                      </div>
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">TCG Market</div>
                        <div className="font-semibold text-amber-300">
                          {money(tcg?.market)}
                        </div>
                      </div>

                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">CK Low</div>
                        <div className="font-semibold">{money(cardkingdom?.low)}</div>
                      </div>
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">CK Mid</div>
                        <div className="font-semibold">{money(cardkingdom?.mid)}</div>
                      </div>
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">CK Market</div>
                        <div className="font-semibold">{money(cardkingdom?.market)}</div>
                      </div>

                      <div className="rounded bg-black/30 p-2 col-span-2 sm:col-span-3">
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-[10px]">Average Market</span>
                          <span className="font-semibold">{money(averageMarket)}</span>
                        </div>
                        <div className="mt-1 text-[10px] text-white/40">
                          {vendorUpdatedAt
                            ? `Updated ${new Date(vendorUpdatedAt).toLocaleString()}`
                            : "Updated —"}
                        </div>
                      </div>
                    </div>
                  )}

                  {!endpointConfigured && (
                    <div className="mt-2 text-xs text-white/60">
                      Add VITE_CARD_PRICES_URL to .env.local to enable live pricing.
                    </div>
                  )}

                  {vendorLoading && (
                    <div className="mt-3 h-10 rounded bg-white/10 animate-pulse" />
                  )}
                </div>
              </div>
            </div>

            {/* Content grid */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Image picker */}
              <div className="sm:col-span-2">
                <label className="text-sm text-gray-200 block">
                  Custom Image (optional)
                </label>
                {previewUrl ? (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-24 h-32 object-cover rounded border border-gray-700"
                    />
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFile(null);
                          setUploadedUrl(null);
                        }}
                      >
                        Remove
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCropOpen(true)}
                        disabled={!previewUrl}
                        title="Crop the selected image"
                      >
                        Crop
                      </Button>
                      {uploadProgress !== null && (
                        <div className="text-xs text-gray-300">
                          Upload: {uploadProgress}%
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setUploadedUrl(null);
                      setFile(f);
                    }}
                    className="mt-2 block w-full text-sm text-gray-200 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-gray-800 file:text-gray-200 hover:file:bg-gray-700"
                  />
                )}
                <div className="mt-1 text-xs text-gray-400">
                  PNG, JPG, or WEBP. Max 10MB.
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  Tip: Take a clear, well‑lit photo of the full card. After selecting, use Crop to frame just the card.
                </div>
              </div>

              {/* Qty / Condition */}
              <div className="text-sm text-gray-200 flex items-center gap-2">
                <label className="text-sm text-gray-200">
                  Qty
                  <input
                    type="number"
                    min={1}
                    className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-1"
                    value={qty}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") return setQty("");
                      const n = Math.floor(Number(v));
                      setQty(!Number.isFinite(n) ? 1 : n);
                    }}
                    onBlur={() => {
                      if (qty === "" || (typeof qty === "number" && qty < 1)) {
                        setQty(1);
                      }
                    }}
                  />
                </label>

                <label className="text-sm text-gray-200">
                  Condition
                  <select
                    className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-1"
                    value={condition}
                    onChange={(e) =>
                      setCondition(e.target.value as Defaults["condition"])
                    }
                  >
                    <option value="NM">NM</option>
                    <option value="LP">LP</option>
                    <option value="MP">MP</option>
                    <option value="HP">HP</option>
                    <option value="DMG">DMG</option>
                  </select>
                </label>
              </div>

              {/* Toggles */}
              <div className="text-sm text-gray-200 flex items-center gap-2">
                <button
                  type="button"
                  aria-pressed={foil}
                  onClick={() => setFoil((v) => !v)}
                  className={[
                    "px-3 py-1 rounded border transition",
                    foil
                      ? "bg-purple-600/20 border-purple-400 text-purple-200"
                      : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
                  ].join(" ")}
                  title="Toggle foil"
                >
                  Foil
                </button>
              </div>

              <div className="text-sm text-gray-200 flex items-center gap-2">
                <button
                  type="button"
                  aria-pressed={sale}
                  onClick={() => { if (!isLGS) setSale(v => !v); }}
 disabled={isLGS}
                  className={[
                    "px-3 py-1 rounded border transition",
                    sale
                      ? "bg-emerald-600/20 border-emerald-400 text-emerald-200"
                      : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
                  ].join(" ")}
                  title="List for sale"
                >
                  For Sale
                </button>
                <button
                  type="button"
                  aria-pressed={trade}
                  onClick={() => { if (!isLGS) setTrade(v => !v); }}
                  disabled={isLGS}
                  className={[
                    "px-3 py-1 rounded border transition",
                    trade
                      ? "bg-fuchsia-600/20 border-fuchsia-400 text-fuchsia-200"
                      : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
                  ].join(" ")}
                  title="List for trade"
                >
                  For Trade
                </button>
              </div>

              {/* Price */}
              <label className="text-sm text-gray-200 sm:col-span-2">
                Price (if listing for sale)
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-1"
                  value={price}
                  onChange={(e) => {
                    setPriceDirty(true);
                    setUserTouchedPrice(true);
                    setPrice(e.target.value === "" ? "" : Number(e.target.value));
                  }}
                  disabled={!sale}
                  placeholder={
                    typeof tcg?.market === "number"
                      ? `Default: $${tcg.market.toFixed(2)} (TCG Market)`
                      : typeof averageMarket === "number"
                      ? `Default: $${averageMarket.toFixed(2)} (Avg)`
                      : "Enter a price or leave blank"
                  }
                />
                <div className="text-xs text-white/50 mt-1">
                  Defaults to current TCGplayer Market when available.
                </div>
              </label>

              {/* Notes */}
              <label className="text-sm text-gray-200 sm:col-span-2">
                Notes
                <textarea
                  className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-1"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special notes (promo, misprint, signature, etc.)"
                />
              </label>

              {/* Remember */}
              <label className="text-sm text-gray-200 sm:col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  className="rounded border-gray-700"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Remember these as my defaults (excludes price)
              </label>

              {/* Binder selector */}
              <div className="mt-3">
                <label className="text-sm text-slate-300">Add to binder</label>
                <select
                  className="mt-1 w-full bg-gray-900 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                  value={binderId}
                  onChange={(e) => setBinderId(e.target.value)}
                >
                  <option value="">None</option>
                  {binders.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <footer className="px-4 sm:px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2 shrink-0">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="outline" onClick={saveToWishlist} disabled={saving}>
              Add to Wishlist
            </Button>
            <Button
              className="btn-pixel btn-pixel-primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Saving…" : "Add to Collection"}
            </Button>
          </footer>
        </div>
      </div>

      {previewUrl && (
        <ImageCropDialog
          open={cropOpen}
          src={previewUrl}
          aspect={3 / 4}
          onCancel={() => setCropOpen(false)}
          onApply={(blob) => {
            const newFile = new File([blob], `crop_${Date.now()}.jpg`, { type: "image/jpeg" });
            setUploadedUrl(null);
            setFile(newFile);
            setCropOpen(false);
          }}
        />
      )}
    </>
  );
}

// Add-to-collection helper using enrichCardMeta (for Search page and bulk flows)
export async function addToCollectionWithEnrichment(
  uid: string,
  form: {
    name: string;
    set?: string | null;
    number?: string | null;
    foil?: boolean;
    qty?: number;
    status?: "keep" | "sale" | "trade";
    price?: number | null;
    scryfall_id?: string | null;
    custom_image_url?: string | null;
  },
): Promise<string> {
  const base = {
    name: form.name,
    set: form.set ?? null,
    number: form.number ?? null,
    foil: !!form.foil,
    qty: form.qty ?? 1,
    status: form.status ?? "keep",
    price: form.price ?? null,
    scryfall_id: form.scryfall_id ?? null,
    custom_image_url: form.custom_image_url ?? null,
  };

  // Enrich before write (instant UX); onCreate trigger also ensures persistence
  const enriched = await enrichCardMeta({
    scryfall_id: base.scryfall_id,
    set: base.set,
    number: base.number,
    existing: {
      image: null,
      custom_image_url: base.custom_image_url,
    },
  });

  const docData = {
    ...base,
    ...enriched,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, "users", uid, "cards"), docData);
  return ref.id;
}